import React, { useEffect, useRef, useState } from 'react';
import GradedCanvas from './GradedCanvas';
import CropBox from './CropBox';
import { fmtTime } from '../utils/movie';
import { composeGrade, DEFAULT_GRADE } from '../utils/grade';
import { rectScale, zoomFromScale } from '../utils/crop';

// A large "contact sheet" of keyframes across the clip, so you can grade and
// reframe/zoom throughout the DURATION with real working space (not a thin
// strip). Each card renders the captured raw frame at that key's time through
// the same WebGL grade pipeline as the main preview, with the key's grade +
// framing applied. In reframe mode the SELECTED card shows the full frame with
// a draggable crop box so you reframe right on the big frame.
//
// WebGL budget: the browser caps live contexts (~16), so cards lazy-mount their
// GradedCanvas only while on/near screen (IntersectionObserver) and release it
// when scrolled away — keeping only the visible handful live at once.
//
// Props:
//   videoRef, videoEl   the shared (hidden) <video> we sample stills from
//   mode                'grade' | 'reframe'
//   items               [{ id, t, grade, rect }] — grade + framing to render
//   selectedId, onSelect(id)
//   layers              { light, color, effects } layer toggles (match preview)
//   geo                 { rotate, flipH, flipV } uniform straighten/flip
//   lockRatio           crop-box aspect lock (reframe mode)
//   onRectChange(id, rect)  reframe edit from a card's crop box
const CAP_W = 820; // captured still width — crisp enough for a big sample card

export default function KeyframeStoryboard({
  videoRef, videoEl, mode, items, selectedId, onSelect, layers, geo, lockRatio, onRectChange,
}) {
  const [frames, setFrames] = useState({}); // id -> { t, bmp }
  const framesRef = useRef({});
  framesRef.current = frames;
  const runningRef = useRef(false);
  const ar = videoEl && videoEl.videoWidth ? videoEl.videoWidth / videoEl.videoHeight : 16 / 9;

  // Serially seek the (hidden) main video to each key time and grab a downscaled
  // still. One <video> → must serialize. Restores the playhead when done.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const v = videoRef.current;
      if (!v || !videoEl || runningRef.current) return;
      const need = items.filter((it) => {
        const cur = framesRef.current[it.id];
        return !cur || Math.abs(cur.t - it.t) > 0.02;
      });
      if (!need.length) return;
      runningRef.current = true;
      const wasPlaying = !v.paused;
      if (wasPlaying) { try { v.pause(); } catch { /* noop */ } }
      const restore = v.currentTime;
      const w = CAP_W;
      const h = Math.max(2, Math.round(w / (ar || 16 / 9)));
      for (const it of need) {
        if (cancelled) break;
        // eslint-disable-next-line no-await-in-loop
        await seekTo(v, it.t);
        let bmp = null;
        try {
          // eslint-disable-next-line no-await-in-loop
          bmp = await createImageBitmap(v, { resizeWidth: w, resizeHeight: h, resizeQuality: 'high' });
        } catch {
          try {
            const c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(v, 0, 0, w, h);
            // eslint-disable-next-line no-await-in-loop
            bmp = await createImageBitmap(c);
          } catch { bmp = null; }
        }
        if (bmp && !cancelled) {
          setFrames((p) => {
            const prev = p[it.id];
            if (prev && prev.bmp && prev.bmp.close) { try { prev.bmp.close(); } catch { /* noop */ } }
            return { ...p, [it.id]: { t: it.t, bmp } };
          });
        }
      }
      try { v.currentTime = restore; } catch { /* noop */ }
      if (wasPlaying && !cancelled) v.play().catch(() => {});
      runningRef.current = false;
    };
    run();
    return () => { cancelled = true; };
  }, [items, videoEl, videoRef, ar]);

  // Drop bitmaps for removed keys (free GPU/CPU memory).
  useEffect(() => {
    setFrames((p) => {
      const ids = new Set(items.map((i) => i.id));
      let changed = false;
      const next = {};
      for (const k of Object.keys(p)) {
        if (ids.has(k)) next[k] = p[k];
        else { changed = true; if (p[k]?.bmp?.close) { try { p[k].bmp.close(); } catch { /* noop */ } } }
      }
      return changed ? next : p;
    });
  }, [items]);

  const cropForCard = (it, isSel) => {
    if (mode === 'reframe' && isSel) {
      // Edit on the full frame so the box has room to move.
      return { x: 0, y: 0, w: 1, h: 1, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV };
    }
    const r = it.rect;
    if (r && r.w > 0) return { ...r, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV };
    if (geo.rotate || geo.flipH || geo.flipV) return { x: 0, y: 0, w: 1, h: 1, rotate: geo.rotate, flipH: geo.flipH, flipV: geo.flipV };
    return null;
  };

  return (
    <div className="cs-storyboard">
      {items.map((it, i) => {
        const isSel = it.id === selectedId;
        const frame = frames[it.id];
        const cropObj = cropForCard(it, isSel);
        const zoomX = it.rect && it.rect.w > 0 ? zoomFromScale(rectScale(it.rect)) : null;
        return (
          <SbCard
            key={it.id}
            ar={ar}
            selected={isSel}
            onSelect={() => onSelect(it.id)}
            label={`${i + 1} · ${fmtTime(it.t)}${zoomX ? ` · ${zoomX.toFixed(2)}×` : ''}`}
          >
            {frame ? (
              <>
                <GradedCanvas
                  source={frame.bmp}
                  grade={composeGrade(it.grade || DEFAULT_GRADE, layers)}
                  getCrop={() => cropObj}
                  maxWidth={CAP_W}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                {mode === 'reframe' && isSel && it.rect && (
                  <CropBox rect={it.rect} lockRatio={lockRatio} onChange={(r) => onRectChange?.(it.id, r)} />
                )}
              </>
            ) : (
              <div className="cs-sb-ph"><span className="cs-spinner" /></div>
            )}
          </SbCard>
        );
      })}
    </div>
  );
}

// One storyboard cell. Mounts its (WebGL) child only while on/near screen so we
// never exceed the browser's live-context cap; shows an aspect-correct
// placeholder otherwise.
function SbCard({ ar, selected, onSelect, label, children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setVisible(e.isIntersecting)),
      { root: null, rootMargin: '300px 0px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <button
      type="button"
      ref={ref}
      className={`cs-sb-card ${selected ? 'sel' : ''}`}
      onClick={onSelect}
      title="Click to edit this keyframe"
    >
      <div className="cs-sb-frame" style={{ aspectRatio: String(ar || 16 / 9) }}>
        {visible ? children : null}
      </div>
      <span className="cs-sb-head">◆ {label}</span>
    </button>
  );
}

function seekTo(v, t) {
  return new Promise((res) => {
    const on = () => { v.removeEventListener('seeked', on); res(); };
    v.addEventListener('seeked', on);
    try { v.currentTime = Math.max(0, t); } catch { res(); }
    setTimeout(() => { v.removeEventListener('seeked', on); res(); }, 1800);
  });
}
